import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useSetupStatus } from "@/hooks/useSetupStatus";

const EASE = [0.22, 1, 0.36, 1];

// Slim "Step x of 2" guide at the top of Team / Projects while setup is incomplete.
export function SetupStepGuide({ page, onAction }) {
  const s = useSetupStatus();
  if (!s || s.steps_left === 0) return null;
  const t = s.team.done;
  const p = s.projects;
  let msg, cta, to, alt;
  if (page === "team") {
    if (!t) {
      const waiting = s.team.invites ? ` ${s.team.invites} invite${s.team.invites === 1 ? " is" : "s are"} still waiting to be accepted.` : "";
      msg = `Step 1 of 2 — get a teammate on the list: create their account with Add teammate, or invite someone who already uses Vektor below.${waiting}`;
      cta = "Add teammate";
      alt = { label: "Invite existing user", kind: "invite" };
    } else { msg = "Nice — you have a teammate. Next: put them on a project."; cta = "Go to Projects"; to = "/app/projects"; }
  } else {
    if (!p.total) { msg = `Step 2 of 2 — create your first project${t ? "" : " (you can add teammates after)"}.`; cta = "Create a project"; }
    else if (!t) { msg = "First, add a teammate on the Team page so you have someone to put on your project."; cta = "Go to Team"; to = "/app/team"; }
    else { msg = `Step 2 of 2 — add your people to ${p.next?.name || "a project"} and pick its lead.`; cta = "Manage its team"; }
  }
  const btn = "inline-flex shrink-0 items-center gap-2 rounded-full bg-[#00FF66] px-4 py-2 text-xs font-bold text-black transition-[transform,background-color] duration-200 hover:translate-x-0.5 hover:bg-[#00cc52]";
  const btnAlt = "inline-flex shrink-0 items-center gap-2 rounded-full border border-[#00FF66]/40 px-4 py-2 text-xs font-bold text-[#00FF66] transition-colors duration-200 hover:bg-[#00FF66]/10";
  return (
    <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }} className="mt-6 flex flex-col gap-3 rounded-2xl border border-[#00FF66]/30 bg-[#00FF66]/[0.05] px-5 py-4 sm:flex-row sm:items-center" data-testid={`setup-guide-${page}`}>
      <span className="relative flex h-2.5 w-2.5 shrink-0"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00FF66] opacity-60" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#00FF66]" /></span>
      <p className="flex-1 text-sm font-medium text-white/80" data-testid={`setup-guide-${page}-text`}>{msg}</p>
      {alt && <button onClick={() => onAction(alt.kind)} className={btnAlt} data-testid={`setup-guide-${page}-alt`}>{alt.label}</button>}
      {to ? (
        <Link to={to} className={btn} data-testid={`setup-guide-${page}-cta`}>{cta} <ArrowRight className="h-3.5 w-3.5" /></Link>
      ) : (
        <button onClick={() => onAction(page === "team" ? "add" : p.next)} className={btn} data-testid={`setup-guide-${page}-cta`}>{cta} <ArrowRight className="h-3.5 w-3.5" /></button>
      )}
    </motion.div>
  );
}

// Pulsing dot for the sidebar while a setup step is open.
export function SetupDot({ testid }) {
  return (
    <span className="relative ml-auto flex h-2 w-2 shrink-0" title="Setup step to finish" data-testid={testid}>
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00FF66] opacity-70" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00FF66]" />
    </span>
  );
}
